import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTable, useSortBy, useFilters } from "react-table";
import { FaSortUp, FaSortDown, FaSort } from "react-icons/fa";

const CalendarEventTable = ({
    data = [],
    styles = {},
    currentPage,
    totalPages,
    handlePreviousPage,
    handleNextPage,
}) => {
    // Custom filter function for type-ahead filtering
    const typeAheadFilter = (rows, id, filterValue) => {
        return rows.filter((row) => 
            row.values[id].toLowerCase().includes(filterValue.toLowerCase())
        );
    };

    const DefaultColumnFilter = ({
        column: { filterValue, setFilter },
    }) => (
        <input
            value={filterValue || ""}
            onChange={(e) => setFilter(e.target.value || undefined)} // Set undefined to clear the filter
            placeholder={`Search...`}
            style={{
                width: "100%",
                padding: "5px",
                boxSizing: "border-box",
            }}
        />
    );

    const columns = useMemo(
        () => [
            {
                Header: "Date",
                accessor: "event_date",
            },
            {
                Header: "Time",
                accessor: "time",
            },
            {
                Header: "Currency",
                accessor: "currency",
            },
            {
                Header: "Event",
                accessor: "event",
                Filter: DefaultColumnFilter, // Add the type-ahead filter
                filter: typeAheadFilter, // Use the custom filter function
            },
            {
                Header: "Impact",
                accessor: "impact",
            },
            {
                Header: "Actual",
                accessor: "actual",
            },
            {
                Header: "Forecast",
                accessor: "forecast",
            },
            {
                Header: "Previous",
                accessor: "previous",
            },
        ],
        []
    );

    const defaultColumn = {
        Filter: DefaultColumnFilter, // Default filter for all columns
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
        },
        useFilters, // Enable filtering
        useSortBy // Enable sorting
    );

    const renderPaginationControls = () => (
        <div className="d-flex justify-content-end align-items-center my-2">
            <button
                className="btn btn-outline-primary btn-sm mx-1"
                onClick={() => handlePreviousPage()}
                disabled={currentPage === 1}
            >
                {"<<"}
            </button>
            <button
                className="btn btn-outline-primary btn-sm mx-1"
                onClick={() => handlePreviousPage()}
                disabled={currentPage === 1}
            >
                {"<"}
            </button>
            <button
                className="btn btn-outline-primary btn-sm mx-1"
                onClick={() => handleNextPage()}
                disabled={currentPage === totalPages}
            >
                {">"}
            </button>
            <button
                className="btn btn-outline-primary btn-sm mx-1"
                onClick={() => handleNextPage()}
                disabled={currentPage === totalPages}
            >
                {">>"}
            </button>
            <span className="mx-2">
                Page <strong>{currentPage}</strong> of {totalPages}
            </span>
        </div>
    );

    return (
        <div className={styles?.tableContainer || "table-container"}>
            {renderPaginationControls()}

            <div className="table-responsive" style={{ marginTop: "1rem" }}>
                <table
                    {...getTableProps()}
                    className={`table table-striped table-hover ${
                        styles?.table || "calendar-event-table"
                    }`}
                >
                    <thead className="table-dark">
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps(column.getSortByToggleProps())} key={column.id}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {column.render("Header")}
                                            {column.canSort && (
                                                <span style={{ marginLeft: 5 }}>
                                                    {column.isSorted ? (
                                                        column.isSortedDesc ? (
                                                            <FaSortDown />
                                                        ) : (
                                                            <FaSortUp />
                                                        )
                                                    ) : (
                                                        <FaSort />
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        {column.canFilter ? column.render("Filter") : null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.map((row) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()} key={row.id}>
                                    {row.cells.map((cell) => (
                                        <td {...cell.getCellProps()} key={cell.column.id}>
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {renderPaginationControls()}
        </div>
    );
};

CalendarEventTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            event_date: PropTypes.string.isRequired,
            time: PropTypes.string,
            currency: PropTypes.string.isRequired,
            event: PropTypes.string.isRequired,
            impact: PropTypes.string,
            actual: PropTypes.string,
            forecast: PropTypes.string,
            previous: PropTypes.string,
        })
    ).isRequired,
    styles: PropTypes.object,
    currentPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    handlePreviousPage: PropTypes.func.isRequired,
    handleNextPage: PropTypes.func.isRequired,
};

export default CalendarEventTable;
